import React, { useState } from 'react';
import styles from './LevelControlFields.module.scss';
import ToggleSwitch from '@/components/school-management/Toggle/Toggle';
import DropDown, { Option } from '@/components/school-management/DropDown/DropDown';
import Popup from '@/components/school-management/Popup/Popup';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import {
  applyPlacementTestToggle,
  getAdvancementConfirmLabel,
  getAdvancementWarning,
  getDefaultStartingLevelConfirmLabel,
  getDefaultStartingLevelWarning,
  getLevelControlOptions,
  getPlacementTestConfirmLabel,
  getPlacementTestWarning,
} from '@/school-management/utils/levelControl';

export interface LevelControlFieldsProps {
  placementTestRequired: boolean;
  defaultStartingLevel: string | null;
  allowIndividualAdvancement: boolean;
  onChange: (values: {
    placementTestRequired: boolean;
    defaultStartingLevel: string | null;
    allowIndividualAdvancement: boolean;
  }) => void;
  defaultStartingLevelError?: string;
  disabled?: boolean;
  /**
   * Suppresses the "Are you sure?" prompt. Not used by the Create Class or Edit Class forms
   * (both confirm every change) - kept as an opt-out for any future caller that genuinely has
   * no existing students to warn about.
   */
  skipConfirmation?: boolean;
  /** Whether this is the Create Class form (no existing students yet) vs. Edit Class. */
  isNewClass: boolean;
}

/**
 * The three per-class level control settings (placementTestRequired, defaultStartingLevel,
 * allowIndividualAdvancement) shared between the admin Create Class and Edit Class forms.
 * Every change - on both Create and Edit - is confirmed with an "Are you sure?" prompt before
 * it's applied. See ELL-7746 / ELL-7727.
 */
const LevelControlFields: React.FC<LevelControlFieldsProps> = ({
  placementTestRequired,
  defaultStartingLevel,
  allowIndividualAdvancement,
  onChange,
  defaultStartingLevelError,
  disabled,
  skipConfirmation = false,
  isNewClass,
}) => {
  const { t } = useAdminTranslation();
  const levelOptions = getLevelControlOptions(t);
  const selectedLevelOption = levelOptions.find(option => option.value === defaultStartingLevel);
  const [pendingChange, setPendingChange] = useState<{
    message: string;
    confirmLabel: string;
    apply: () => void;
  } | null>(null);

  const confirmChange = (message: string, confirmLabel: string, apply: () => void) => {
    if (skipConfirmation) {
      apply();
      return;
    }
    setPendingChange({ message, confirmLabel, apply });
  };

  return (
    <div className={styles.levelControlFields}>
      <ToggleSwitch
        label={t('schoolDetailsPage.classes.levelControl.placementTestRequiredLabel') || ''}
        subTitle={t('schoolDetailsPage.classes.levelControl.placementTestRequiredTooltip') || ''}
        checked={placementTestRequired}
        onChange={(value: boolean) => {
          confirmChange(
            getPlacementTestWarning(value, isNewClass, t),
            getPlacementTestConfirmLabel(value, t),
            () => {
              onChange(
                applyPlacementTestToggle(
                  { placementTestRequired, defaultStartingLevel, allowIndividualAdvancement },
                  value
                )
              );
            }
          );
        }}
      />

      {!placementTestRequired && (
        <div className={styles.dropdownWrapper}>
          <DropDown
            label={`${t('schoolDetailsPage.classes.levelControl.defaultStartingLevelLabel')} *`}
            placeholder={
              t('schoolDetailsPage.classes.levelControl.defaultStartingLevelPlaceholder') || ''
            }
            options={levelOptions as Option[]}
            value={selectedLevelOption as Option | undefined}
            disabled={disabled}
            onChange={(option: Option) => {
              confirmChange(
                getDefaultStartingLevelWarning(isNewClass, t),
                getDefaultStartingLevelConfirmLabel(isNewClass, t),
                () => {
                  onChange({
                    placementTestRequired,
                    defaultStartingLevel: option.value,
                    allowIndividualAdvancement,
                  });
                }
              );
            }}
          />
          {defaultStartingLevelError && (
            <div className={styles.errorMessage}>{defaultStartingLevelError}</div>
          )}
        </div>
      )}

      <ToggleSwitch
        label={t('schoolDetailsPage.classes.levelControl.allowIndividualAdvancementLabel') || ''}
        subTitle={
          t('schoolDetailsPage.classes.levelControl.allowIndividualAdvancementTooltip') || ''
        }
        checked={allowIndividualAdvancement}
        onChange={(value: boolean) => {
          confirmChange(
            getAdvancementWarning(value, isNewClass, t),
            getAdvancementConfirmLabel(value, t),
            () => {
              onChange({
                placementTestRequired,
                defaultStartingLevel,
                allowIndividualAdvancement: value,
              });
            }
          );
        }}
      />

      <Popup
        open={Boolean(pendingChange)}
        onClose={() => setPendingChange(null)}
        onEnable={() => {
          pendingChange?.apply();
          setPendingChange(null);
        }}
        title={t('schoolDetailsPage.classes.levelControl.confirmTitle') || ''}
        titleClassName={styles.confirmTitle}
        message={pendingChange?.message}
        contentClassName={styles.confirmContent}
        actionsClassName={styles.confirmActions}
        confirmLabel={
          pendingChange?.confirmLabel ||
          t('schoolDetailsPage.classes.levelControl.confirmButton') ||
          ''
        }
        confirmButtonClassName={styles.confirmButton}
        cancelLabel={t('schoolDetailsPage.classes.levelControl.cancelButton') || ''}
        cancelButtonClassName={styles.cancelButton}
        modalClassName={styles.confirmModal}
        showCheckbox={false}
      />
    </div>
  );
};

export default LevelControlFields;
